import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTruck, faChevronLeft, faTimes } from '@fortawesome/pro-light-svg-icons';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Product } from '@shared/components/ui/Product';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { GroupOrdersModal, GroupOrdersModalRef } from '../modals/GroupOrdersModal';
import { PrepareOrdersModal, PrepareOrdersModalRef } from '../modals/PrepareOrdersModal';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@shared/components/ui/shadcn/accordion';
import { useToast } from '@shared/hooks/use-toast';
import { useUserData } from '@shared/hooks/useUser';
import { useIsMobile } from '@shared/hooks/useMobile';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import type { HeaderAction } from '@shared/components/navigation/types';
import { useTranslation } from 'react-i18next';
import {
  PreparationType,
  PreparationStatus,
  getPreparationStatusColor,
  usePreparationStatusName,
} from '@modules/OrderPreparation/shared/config';
import { useMassivePacking, useProductLocations } from '../../hooks';
import type { MassivePackingGroup } from '../../models';
import { SelectionToolbar } from '../interaction';

// 🎯 Componente contenedor principal para la pantalla de Packing Masivo
export const Main: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('massivePackingModule');
  const { t: tOrderPrep } = useTranslation('orderpreparationModule');
  const isMobile = useIsMobile();
  const preparationId = id ? parseInt(id, 10) : 0;

  const { data: userData } = useUserData();
  const teamId = userData?.team?.id ?? 0;

  const { preparation, groups, isLoading, isFetching, error, refetch, service } = useMassivePacking(preparationId);

  // Precargar ubicaciones de todos los productos cuando se cargan los grupos
  const { locationsByProduct } = useProductLocations(groups);

  const [selectedGroupKeys, setSelectedGroupKeys] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [printingGroupKey, setPrintingGroupKey] = useState<string | null>(null);
  const confirmModalRef = React.useRef<ConfirmModalRef>(null);
  const processConfirmRef = React.useRef<ConfirmModalRef>(null);
  const cancelConfirmRef = React.useRef<ConfirmModalRef>(null);
  const groupOrdersModalRef = React.useRef<GroupOrdersModalRef>(null);
  const prepareOrdersModalRef = React.useRef<PrepareOrdersModalRef>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const isBusy = isCancelling || isProcessing || isFetching;

  const statusName = usePreparationStatusName(preparation?.status ?? 0);
  const statusColor = getPreparationStatusColor(preparation?.status ?? 0);

  // 🎯 Breadcrumbs y acciones en la barra del layout (Volver y Cancelar a la derecha)
  const breadcrumbs = useMemo(() => {
    if (!preparation) return [];
    const base = [
      ...(isMobile
        ? []
        : [
            {
              label: tOrderPrep('breadcrumb.order_preparation'),
              path: '/order-preparation',
            },
          ]),
      {
        _key: `${preparation?.code ?? String(preparation?.id)}-${preparation?.status}`,
        label: (
          <div className="flex items-center gap-3 text-title">
            <span>
              {t('header.title')} {preparation.code}
            </span>
            <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusColor}`}>
              {statusName}
            </Badge>
          </div>
        ),
      },
    ];
    return base;
  }, [preparation, isMobile, tOrderPrep, t, statusName, statusColor]);

  const isComplete = preparation?.status === PreparationStatus.COMPLETE;
  const hasNoGroups = groups.length === 0;

  // Confirmar cancelación de preparación: PUT /preparations/{id}/cancel (204 No Content)
  const handleConfirmCancelPreparation = useCallback(async () => {
    if (!preparation) return;
    setIsCancelling(true);
    try {
      await service.getAdapter().cancelPreparation(preparation.id);
      toast({
        title: t('toasts.cancel_success_title'),
        description: t('toasts.cancel_success_description'),
        variant: 'default',
      });
      // Actualizar lista principal en segundo plano (sin await para navegar rápido)
      queryClient.refetchQueries({
        queryKey: ['orderpreparation'],
        type: 'all',
      });

      // Invalidar query de esta preparación para que al volver a entrar se actualice
      queryClient.invalidateQueries({
        queryKey: ['massivePacking', preparation.id],
      });

      navigate('/order-preparation');
    } catch (e: any) {
      toast({
        title: t('toasts.cancel_error_title'),
        description: e?.message ?? t('toasts.cancel_error_description'),
        variant: 'destructive',
      });
    } finally {
      setIsCancelling(false);
    }
  }, [preparation, preparationId, service, toast, queryClient, t]);

  const handleBackClick = useCallback(() => {
    navigate('/order-preparation');
  }, [navigate]);

  const handleCancelPreparationClick = useCallback(() => {
    cancelConfirmRef.current?.open(
      handleConfirmCancelPreparation,
      undefined,
      t('modals.cancel_preparation.title'),
      t('modals.cancel_preparation.message'),
      t('actions.confirm'),
      t('actions.cancel'),
    );
  }, [handleConfirmCancelPreparation, t]);

  const showCancelButton = !isComplete && !hasNoGroups;

  const headerActions = useMemo((): HeaderAction[] => {
    const actions: HeaderAction[] = [
      {
        id: 'back',
        label: t('actions.back'),
        onClick: handleBackClick,
        variant: 'primary',
        disabled: isBusy,
        component: (
          <Button
            variant="primary"
            onClick={handleBackClick}
            disabled={isBusy}
            data-testid="header-action-back"
            className="max-md:px-2 max-md:min-w-9"
            title={t('actions.back')}
          >
            <span className="flex items-center gap-2">
              <span className="hidden md:inline">{t('actions.back')}</span>
              <span className="md:hidden w-5 h-5 flex items-center justify-center">
                <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
              </span>
            </span>
          </Button>
        ),
      },
    ];
    if (showCancelButton) {
      actions.push({
        id: 'cancel',
        label: t('actions.cancel_placeholder'),
        onClick: handleCancelPreparationClick,
        variant: 'outline',
        disabled: isBusy,
        component: (
          <Button
            variant="outline"
            onClick={handleCancelPreparationClick}
            disabled={isBusy}
            data-testid="header-action-cancel"
            className="max-md:px-2 max-md:min-w-9"
            title={t('actions.cancel_placeholder')}
          >
            <span className="flex items-center gap-2">
              <span className="hidden md:inline">{t('actions.cancel_placeholder')}</span>
              <span className="md:hidden w-5 h-5 flex items-center justify-center">
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </span>
            </span>
          </Button>
        ),
      });
    }
    return actions;
  }, [t, showCancelButton, handleBackClick, handleCancelPreparationClick, isBusy]);

  usePageNavigation({ breadcrumbs, headerActions });

  // 🎯 Todos los hooks deben ir antes de cualquier return condicional (reglas de React)
  const selectedGroups: MassivePackingGroup[] = useMemo(
    () => groups.filter((g) => selectedGroupKeys.has(g.groupKey)),
    [groups, selectedGroupKeys],
  );

  const preparableGroups = useMemo(
    () =>
      selectedGroups.filter((group) => {
        const preparedOrders = group.orders.filter((order) => !!order.shipment?.processed_at).length;
        // Preparable si NO todos están preparados (o sea, queda algo por preparar)
        return preparedOrders < group.orders.length;
      }),
    [selectedGroups],
  );

  const printableGroups = useMemo(
    () =>
      selectedGroups.filter((group) => {
        const preparedOrders = group.orders.filter((order) => !!order.shipment?.processed_at).length;
        // Imprimible si hay AL MENOS UNO preparado
        return preparedOrders > 0;
      }),
    [selectedGroups],
  );

  // Validar ID de preparación
  if (!preparationId || preparationId <= 0) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-error mb-4">{t('errors.invalid_preparation_id')}</h1>
          <Button
            variant="outline"
            onClick={() => navigate('/order-preparation')}
            data-testid="back-to-preparations-button"
          >
            {t('actions.back_to_list')}
          </Button>
        </div>
      </div>
    );
  }

  // Estados de carga
  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-subtext">{t('states.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Estados de error de carga
  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-error mb-4">{t('errors.loading_failed')}</h1>
          <p className="text-subtext mb-6">{error}</p>
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/order-preparation')}
              data-testid="back-to-preparations-button"
            >
              {t('actions.back_to_list')}
            </Button>
            <Button variant="primary" onClick={() => refetch()} data-testid="retry-loading-button">
              {t('actions.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Preparación no encontrada
  if (!preparation) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-subtext mb-4">
            {t('errors.preparation_not_found', { id: preparationId })}
          </h1>
          <Button
            variant="outline"
            onClick={() => navigate('/order-preparation')}
            data-testid="back-to-preparations-button"
          >
            {t('actions.back_to_list')}
          </Button>
        </div>
      </div>
    );
  }

  // Validar tipo de preparación: solo MASSIVE_PACKING
  if (preparation.type !== PreparationType.MASSIVE_PACKING) {
    toast({
      title: t('errors.invalid_type_title'),
      description: t('errors.invalid_type_description'),
      variant: 'destructive',
    });
    navigate('/order-preparation');
    return null;
  }

  const handleToggleGroup = (groupKey: string) => {
    setSelectedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedGroupKeys(new Set());
  };

  const handlePrintGroupLabels = async (group: MassivePackingGroup) => {
    const preparedOrders = group.orders.filter((order) => !!order.shipment?.processed_at);
    if (preparedOrders.length === 0) return;

    setPrintingGroupKey(group.groupKey);
    const adapter = service.getAdapter();
    let printedCount = 0;

    try {
      for (const order of preparedOrders) {
        const success = await printOrderLabel(order.id, adapter);
        if (success) printedCount++;
      }

      if (printedCount > 0) {
        toast({
          title: t('toasts.print_success_title'),
          description:
            printedCount === 1
              ? t('toasts.print_success_description')
              : t('toasts.print_success_many_description', {
                  count: printedCount,
                }),
          variant: 'default',
        });
      }
    } catch (e: any) {
      toast({
        title: t('toasts.print_error_title'),
        description: e?.message ?? t('toasts.print_error_description'),
        variant: 'destructive',
      });
    } finally {
      setPrintingGroupKey(null);
    }
  };

  const handleOpenGroupOrders = (group: MassivePackingGroup) => {
    groupOrdersModalRef.current?.open(group);
  };

  const handlePrintSingleOrder = async (order: any) => {
    const adapter = service.getAdapter();
    try {
      const success = await printOrderLabel(order.id, adapter);
      if (success) {
        toast({
          title: t('toasts.print_success_title'),
          description: t('toasts.print_success_description'),
          variant: 'default',
        });
      }
    } catch (e: any) {
      toast({
        title: t('toasts.print_error_title'),
        description: e?.message ?? t('toasts.print_error_description'),
        variant: 'destructive',
      });
    }
  };

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      setSelectedGroupKeys(new Set(groups.map((g) => g.groupKey)));
    } else {
      setSelectedGroupKeys(new Set());
    }
  };

  const handleProcessSelected = async () => {
    if (!preparableGroups.length) {
      toast({
        title: t('errors.no_group_selected_title'),
        description: t('errors.no_group_selected_description'),
        variant: 'destructive',
      });
      return;
    }

    if (!teamId) {
      toast({
        title: t('errors.missing_team_title'),
        description: t('errors.missing_team_description'),
        variant: 'destructive',
      });
      return;
    }

    try {
      // Obtener todos los productos únicos de los grupos seleccionados
      const uniqueProductIds = Array.from(
        new Set(preparableGroups.flatMap((group) => group.products.map((p) => p.productId))),
      );

      // Filtrar solo las ubicaciones de los productos seleccionados
      // (las ubicaciones ya están precargadas por el hook useProductLocations)
      const filteredLocationsByProduct = Object.fromEntries(
        uniqueProductIds
          .filter((productId) => locationsByProduct[productId])
          .map((productId) => [productId, locationsByProduct[productId]]),
      );

      // Abrir modal de preparación con los grupos y ubicaciones (ya precargadas)
      prepareOrdersModalRef.current?.open(preparableGroups, filteredLocationsByProduct);
    } catch (e: any) {
      toast({
        title: t('toasts.process_error_title'),
        description: e?.message ?? t('toasts.process_error_description'),
        variant: 'destructive',
      });
    }
  };

  // Función que se ejecuta cuando el usuario confirma en el modal de preparación
  const handleConfirmPrepareOrders = async (payload: {
    orders: Array<{
      order_id: number;
      products: Array<{
        product_id: number;
        locations: Array<{
          location_id: number;
          quantity: number;
        }>;
      }>;
    }>;
  }) => {
    if (!preparation || !teamId) {
      toast({
        title: t('errors.missing_team_title'),
        description: t('errors.missing_team_description'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsProcessing(true);

      const adapter = service.getAdapter();

      const fullPayload = {
        team_id: teamId,
        preparation_id: preparation.id,
        orders: payload.orders,
      };

      await adapter.massivePack(preparation.id, fullPayload);

      toast({
        title: t('toasts.process_success_title'),
        description: t('toasts.process_success_description'),
        variant: 'default',
      });

      // Actualizar lista de preparaciones (api/v1/preparations) para que la tabla principal refleje cambios
      await queryClient.refetchQueries({
        queryKey: ['orderpreparation'],
        type: 'all',
      });

      await refetch();
      // Limpiar selección tras éxito
      handleClearSelection();
    } catch (e: any) {
      toast({
        title: t('toasts.process_error_title'),
        description: e?.message ?? t('toasts.process_error_description'),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Función auxiliar para imprimir una etiqueta individual
  const printOrderLabel = async (orderId: number, adapter: any) => {
    try {
      const response = await adapter.getOrderPrintLink(orderId, 'pdf');

      let linkUrl = null;
      if (typeof response === 'string') {
        linkUrl = response;
      } else if (Array.isArray(response) && response.length > 0) {
        linkUrl = response[0];
      } else if (response && response.link) {
        linkUrl = response.link;
      }

      if (linkUrl) {
        const finalUrl = String(linkUrl).trim();
        if (finalUrl && finalUrl.startsWith('http')) {
          window.open(finalUrl, '_blank', 'noopener,noreferrer');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error printing label:', error);
      throw error;
    }
  };

  // Imprimir etiquetas masivas de los grupos seleccionados (solo los preparados)
  const handlePrintSelected = async () => {
    if (!printableGroups.length) return;

    // Recopilar todas las órdenes preparadas de los grupos seleccionados
    const ordersToPrint = printableGroups.flatMap((group) =>
      group.orders.filter((order) => !!order.shipment?.processed_at),
    );

    if (!ordersToPrint.length) return;

    const fullMessage = `${t('modals.print_confirm.message')}\n\n${t('modals.print_confirm.sub_message', {
      count: ordersToPrint.length,
    })}`;

    confirmModalRef.current?.open(
      async () => {
        setIsProcessing(true);
        const adapter = service.getAdapter();
        let printedCount = 0;

        try {
          for (const order of ordersToPrint) {
            const success = await printOrderLabel(order.id, adapter);
            if (success) printedCount++;
          }

          if (printedCount > 0) {
            toast({
              title: t('toasts.print_success_title'),
              description:
                printedCount === 1
                  ? t('toasts.print_success_description')
                  : t('toasts.print_success_many_description', {
                      count: printedCount,
                    }),
              variant: 'default',
            });
          }
        } catch (e: any) {
          toast({
            title: t('toasts.print_error_title'),
            description: e?.message ?? t('toasts.print_error_description'),
            variant: 'destructive',
          });
        } finally {
          setIsProcessing(false);
        }
      },
      undefined,
      t('modals.print_confirm.title'),
      fullMessage,
      t('actions.confirm'),
      t('actions.cancel'),
    );
  };

  const totalSelectedOrders = selectedGroups.reduce((acc, group) => acc + group.orders.length, 0);

  return (
    <div className="container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col">
      {/* Contenido principal con accordion */}
      <div
        className={`flex-1 min-h-0 flex flex-col rounded-lg transition-opacity duration-200 ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {/* Header con seleccionar todos - Solo se muestra si hay grupos */}
        {groups.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-card-border">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={!!groups.length && selectedGroupKeys.size === groups.length}
                onCheckedChange={handleSelectAll}
                className="w-4 h-4"
                name="select-all-groups"
              />
              <span className="text-sm font-medium text-title">{t('groups.select_all')}</span>
            </div>
            <span className="text-xs text-subtext">{t('groups.count', { count: groups.length })}</span>
          </div>
        )}

        {/* Accordion con grupos o EmptyView */}
        <div className="flex-1 overflow-y-auto">
          {!groups.length ? (
            <div className="flex items-center justify-center h-full">
              <EmptyView
                type="emptyState"
                icon={EMPTY_VIEW_ICONS.ORDER_PREPARATION}
                title={t('groups.empty')}
                description=""
              />
            </div>
          ) : (
            <Accordion type="multiple" className="w-full">
              {groups.map((group) => {
                const isSelected = selectedGroupKeys.has(group.groupKey);
                const preparedOrders = group.orders.filter((order) => !!order.shipment?.processed_at).length;

                return (
                  <AccordionItem key={group.groupKey} value={group.groupKey} className="border-b border-card-border">
                    <AccordionTrigger className="px-4 hover:no-underline">
                      <div className="flex flex-col gap-2 w-full pr-4 md:flex-row md:gap-0 md:items-center md:justify-between">
                        {/* Fila 1: checkbox + título (+ badge en desktop) */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleToggleGroup(group.groupKey)}
                              className="w-4 h-4 flex-shrink-0"
                              name={`group-checkbox-${group.groupKey}`}
                            />
                          </div>
                          <div className="flex flex-col gap-1 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-title">
                                {t('groups.group_title', {
                                  orders: group.orders.length,
                                })}
                              </span>
                              {/* Badge solo en desktop - en mobile va en la segunda fila */}
                              <Badge
                                variant="outline"
                                className={`hidden md:inline-flex text-xs border-0 ${
                                  preparedOrders > 0
                                    ? 'bg-bg-success text-success'
                                    : getPreparationStatusColor(PreparationStatus.TO_PREPARE)
                                }`}
                              >
                                {preparedOrders > 0 ? t('groups.prepared_badge') : t('groups.to_prepare_badge')}
                              </Badge>
                            </div>
                          </div>
                          {/* Botones solo en desktop */}
                          <div
                            className="hidden md:flex items-center gap-2 flex-shrink-0 ml-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-title hover:text-complementary-text hover:bg-transparent"
                              onClick={() => handleOpenGroupOrders(group)}
                              title={t('modals.group_orders.title')}
                            >
                              <div className="flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faTruck} className="w-3.5 h-3.5" />
                                <span className="font-medium underline decoration-1 underline-offset-2">
                                  {group.orders.length}
                                </span>
                              </div>
                            </Button>
                            {preparedOrders > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="p-2 h-8 w-8 text-icons hover:text-title disabled:opacity-50 border-card-border"
                                disabled={printingGroupKey === group.groupKey}
                                onClick={() => handlePrintGroupLabels(group)}
                                title={t('actions.print_labels')}
                                data-testid={`group-print-labels-${group.groupKey}`}
                              >
                                <FontAwesomeIcon
                                  icon={faPrint}
                                  className={`!w-3 !h-3 ${printingGroupKey === group.groupKey ? 'animate-pulse' : ''}`}
                                />
                              </Button>
                            )}
                          </div>
                        </div>
                        {/* Fila 2 solo en mobile: badge + botones */}
                        <div
                          className="flex md:hidden items-center gap-2 flex-wrap pl-7"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Badge
                            variant="outline"
                            className={`md:hidden text-xs border-0 ${
                              preparedOrders > 0
                                ? 'bg-bg-success text-success'
                                : getPreparationStatusColor(PreparationStatus.TO_PREPARE)
                            }`}
                          >
                            {preparedOrders > 0 ? t('groups.prepared_badge') : t('groups.to_prepare_badge')}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-title hover:text-complementary-text hover:bg-transparent"
                            onClick={() => handleOpenGroupOrders(group)}
                            title={t('modals.group_orders.title')}
                          >
                            <div className="flex items-center gap-1.5">
                              <FontAwesomeIcon icon={faTruck} className="w-3.5 h-3.5" />
                              <span className="font-medium underline decoration-1 underline-offset-2">
                                {group.orders.length}
                              </span>
                            </div>
                          </Button>
                          {preparedOrders > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="p-2 h-8 w-8 text-icons hover:text-title disabled:opacity-50 border-card-border"
                              disabled={printingGroupKey === group.groupKey}
                              onClick={() => handlePrintGroupLabels(group)}
                              title={t('actions.print_labels')}
                              data-testid={`group-print-labels-mobile-${group.groupKey}`}
                            >
                              <FontAwesomeIcon
                                icon={faPrint}
                                className={`!w-3 !h-3 ${printingGroupKey === group.groupKey ? 'animate-pulse' : ''}`}
                              />
                            </Button>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {/* Contenido de cada pedido - alineado con el título del grupo (tras checkbox) */}
                      <div className="pl-7">
                        <h3 className="text-sm font-semibold text-title mb-3">{t('detail.products_title')}</h3>
                        <div className="space-y-2">
                          {group.products.map((product) => (
                            <div
                              key={product.productId}
                              className="flex items-center justify-between text-sm border border-card-border rounded-md px-3 py-2 gap-3"
                            >
                              {/* Product Component - Componente Product */}
                              <div className="flex-1 min-w-0">
                                <Product
                                  id={product.productId}
                                  src={product.imageHash || ''}
                                  name={
                                    product.productName ||
                                    t('detail.unknown_product', {
                                      id: product.productId,
                                    })
                                  }
                                  code={product.productCode}
                                  acronym={product.productName ? product.productName.charAt(0).toUpperCase() : '?'}
                                  showTooltip={true}
                                  enableLineClamp={true}
                                  showImageModal={true}
                                  data-testid={`detail-product-${product.productId}`}
                                />
                              </div>
                              {/* Cantidades - Cantidades */}
                              <div className="flex-shrink-0">
                                <Badge
                                  variant="secondary"
                                  className="text-xs bg-secondary/50 border-secondary text-foreground h-6 min-w-[1.5rem] flex items-center justify-center"
                                >
                                  {product.perOrderQuantity}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      </div>

      {/* Selection Toolbar - aparece solo cuando hay grupos seleccionados */}
      <SelectionToolbar
        selectedCount={selectedGroups.length}
        totalOrdersSelected={totalSelectedOrders}
        onClose={handleClearSelection}
        onPrepareSelected={handleProcessSelected}
        onPrintSelected={handlePrintSelected}
        preparableCount={preparableGroups.length}
        printableCount={printableGroups.length}
        isProcessing={isBusy}
      />

      <ConfirmModal ref={confirmModalRef} />
      <ConfirmModal ref={processConfirmRef} />
      <ConfirmModal ref={cancelConfirmRef} />
      <GroupOrdersModal ref={groupOrdersModalRef} onPrintOrder={handlePrintSingleOrder} />
      <PrepareOrdersModal ref={prepareOrdersModalRef} onConfirm={handleConfirmPrepareOrders} />
    </div>
  );
};
